import {ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal} from "@angular/core";
import {ResponsiveService} from "../../responsive/responsive.service";
import {ButtonShape, ButtonStyle} from "../../configuration/app-entity.model";
import {ButtonConnotation, ButtonLayout, ButtonProminence, ButtonTier} from "./button";
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {IconComponent} from "../../ui-elements/icon/icon.component";
import {SpinnerComponent} from "../../ui-elements/spinner/spinner.component";
import {TouchableDirective} from "../../ui-elements/touchable.directive";
import {HtmlPipe} from '../../utils/html.pipe';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-button",
    templateUrl: "./button.component.html",
    styleUrl: "./button.component.scss",
    host: {
        "[class.woa-button]": "true",
        "[class.override-flat-background-color]": "overrideFlatBackgroundColor()",
        "[class.override-flat-foreground-color]": "overrideFlatForegroundColor()",
        "[class.override-outline-background-color]": "overrideOutlineBackgroundColor()",
        "[class.override-outline-foreground-color]": "overrideOutlineForegroundColor()",
        "[class.override-outline-border-color]": "overrideOutlineBorderColor()",
        "[class.override-raised-background-color]": "overrideRaisedBackgroundColor()",
        "[class.override-raised-foreground-color]": "overrideRaisedForegroundColor()",
        "[class.disabled]": "disabled()",
        "[class.invisible]": "invisible()",
        "[class.hovered]": "hovered()",
        "[class.tier-first]": "this.tier() === undefined || this.tier() === ButtonTier.FIRST",
        "[class.tier-second]": "this.tier() === ButtonTier.SECOND",
        "[class.prominence-high]": "this.prominence() === undefined || this.prominence() === ButtonProminence.HIGH",
        "[class.prominence-low]": "this.prominence() === ButtonProminence.LOW",
        "[class.connotation-affirmative]": "this.connotation() === undefined || this.connotation() === ButtonConnotation.AFFIRMATIVE",
        "[class.connotation-dissuasive]": "this.connotation() === ButtonConnotation.DISSUASIVE",
        "[class.connotation-negative]": "this.connotation() === ButtonConnotation.NEGATIVE",
        "[class.shape-square]": "this.displayShape() === ButtonShape.SQUARE",
        "[class.shape-rounded]": "this.displayShape() === ButtonShape.ROUNDED",
        "[class.shape-oval]": "this.displayShape() === ButtonShape.OVAL",
        "[class.style-flat]": "this.displayStyle() === ButtonStyle.FLAT",
        "[class.style-outline]": "this.displayStyle() === ButtonStyle.OUTLINE",
        "[class.style-raised]": "this.displayStyle() === ButtonStyle.RAISED",
        "[class.layout-vertical]": "this.layout() === ButtonLayout.VERTICAL",
        "[class.layout-horizontal]": "this.layout() === ButtonLayout.HORIZONTAL"
    },
    imports: [
        HtmlPipe,
        IconComponent,
        NgClass,
        SpinnerComponent,
        TouchableDirective,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ButtonComponent extends ActivatableDirective {
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);

    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    ButtonStyle = ButtonStyle;
    ButtonShape = ButtonShape;
    ButtonLayout = ButtonLayout;

    /**
     * Tier, prominence, and connotation classify the set of possible logical buttons.
     * These are the values which are typically specified in the template using the button.
     */
    public readonly tier: InputSignal<ButtonTier> = input<ButtonTier>(ButtonTier.FIRST);
    public readonly prominence: InputSignal<ButtonProminence> = input<ButtonProminence>(ButtonProminence.HIGH);
    public readonly connotation: InputSignal<ButtonConnotation> = input<ButtonConnotation>(ButtonConnotation.AFFIRMATIVE);
    public readonly shape: InputSignal<ButtonShape | undefined> = input<ButtonShape | undefined>();
    public readonly style: InputSignal<ButtonStyle | undefined> = input<ButtonStyle | undefined>();

    /**
     * Support for custom color overrides.
     */
    public readonly overrideFlatBackgroundColor: InputSignal<boolean> = input<boolean>(false); // Set this and set --woa-button-flat-background-color
    public readonly overrideFlatForegroundColor: InputSignal<boolean> = input<boolean>(false); // Set this and set --woa-button-flat-foreground-color

    public readonly overrideOutlineBackgroundColor: InputSignal<boolean> = input<boolean>(false); // Set this and set --woa-button-outline-background-color
    public readonly overrideOutlineForegroundColor: InputSignal<boolean> = input<boolean>(false); // Set this and set --woa-button-outline-foreground-color
    public readonly overrideOutlineBorderColor: InputSignal<boolean> = input<boolean>(false); // Set this and set --woa-button-outline-border-color

    public readonly overrideRaisedBackgroundColor: InputSignal<boolean> = input<boolean>(false); // Set this and set --woa-button-raised-background-color
    public readonly overrideRaisedForegroundColor: InputSignal<boolean> = input<boolean>(false); // Set this and set --woa-button-raised-foreground-color

    /**
     * Button contents and state.
     */
    public readonly label: InputSignal<string> = input<string>();
    public readonly translationParams: InputSignal<Record<string, string | number>> = input<Record<string, string | number>>();
    public readonly icon: InputSignal<string> = input<string>();
    public readonly showSpinner: InputSignal<boolean> = input<boolean>(false);
    public readonly layout: InputSignal<ButtonLayout> = input<ButtonLayout>(ButtonLayout.HORIZONTAL);
    public readonly disabled: InputSignal<boolean> = input<boolean>(false);
    public readonly invisible: InputSignal<boolean> = input<boolean>(false);
    public readonly hovered: InputSignal<boolean> = input<boolean>(false);

    /**
     * Shape, size, style, and color, control the set of possible physical buttons.
     * They are not typically specified directly, but rather derived from the app config based on button tier, prominence, and connotation.
     * Shape is configured per-tier in the app-config.
     * Default size parameters are defined per-tier in default-theme.scss, and configurable in the app-config.
     * Style is configured per-prominence/tier combination in the app-config.
     * Color usage is derived per style/connotation combination in button.component.scss.
     */
    public readonly displayShape: Signal<ButtonShape | undefined> = computed(() =>
        this.shape() ? this.shape()
            : !this.configurationModel.app() ? undefined
                : this.tier() === ButtonTier.FIRST || !this.configurationModel.app()!.secondTierButtonShape
                    ? this.configurationModel.app()!.firstTierButtonShape
                    : this.configurationModel.app()!.secondTierButtonShape);

    public readonly displayStyle: Signal<ButtonStyle | undefined> = computed(() =>
        this.style() ? this.style()
            : !this.configurationModel.app() ? undefined
                : this.prominence() === ButtonProminence.LOW
                    ? (this.tier() === ButtonTier.FIRST ? this.configurationModel.app()!.firstTierLowProminenceButtonStyle : this.configurationModel.app()!.secondTierLowProminenceButtonStyle)
                    : (this.tier() === ButtonTier.FIRST ? this.configurationModel.app()!.firstTierHighProminenceButtonStyle : this.configurationModel.app()!.secondTierHighProminenceButtonStyle));
}
